import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { assignToAdvertiser, listAdvertisers } from "@/api/commerce";
import { apiErrorMessage } from "@/lib/api";
import type { AdvertiserEntityType } from "@/types/commerce";
import { AdvertiserSelect } from "@/components/advertiser-select";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Reassign-advertiser dialog. Triggered from advertiser-owned entity
 * detail pages (page / page-draft / domain / banner). Wraps an
 * {@link AdvertiserSelect} with a required reason textarea — every
 * reassignment lands a row in `advertiser_assignment_log` so the reason
 * isn't optional for audit purposes (the backend allows null but we
 * require it at the UI to keep the log meaningful).
 *
 * Submits via {@link assignToAdvertiser}, then invalidates queries
 * keyed on entityType so detail/list pages refetch the new owner.
 */
export interface ReassignAdvertiserPopoverProps {
    entityType: AdvertiserEntityType;
    entityId: number;
    /** Current advertiser id — `null` means currently unassigned. */
    currentAdvertiserId: number | null;
    /** Render-prop trigger; the parent supplies the button. */
    trigger: React.ReactNode;
    onSuccess?: () => void;
    /**
     * Optional: override the default "Advertiser updated" success toast.
     * Used by the constructor header to surface snapshot-spend semantics:
     * "Future tool spend attributes to {new}. Already-spent stays with {old}."
     */
    successToast?: (args: { oldName: string; newName: string }) => {
        title: string;
        description?: string;
        variant?: "success" | "warning";
    };
    "data-testid"?: string;
}

const ENTITY_LABEL: Record<AdvertiserEntityType, string> = {
    page: "page",
    page_draft: "draft",
    domain: "domain",
    banner: "banner",
};

/** Query keys that should be invalidated after a successful reassign. */
const INVALIDATE_KEYS: Record<AdvertiserEntityType, string[][]> = {
    page: [["pages"], ["page"]],
    page_draft: [["constructor-drafts"], ["constructor-draft"], ["drafts"], ["draft"]],
    domain: [["domains"], ["domain"]],
    banner: [["banners"], ["banner"]],
};

export function ReassignAdvertiserPopover({
    entityType,
    entityId,
    currentAdvertiserId,
    trigger,
    onSuccess,
    successToast,
    "data-testid": testId,
}: ReassignAdvertiserPopoverProps): React.ReactNode {
    const queryClient = useQueryClient();
    const [open, setOpen] = React.useState(false);
    const [advertiserId, setAdvertiserId] = React.useState<number | null>(currentAdvertiserId);
    const [reason, setReason] = React.useState("");

    // Cached list — used both for the rename toast ("Acme → BrandX")
    // and as a fallback when the parent doesn't pre-resolve the name.
    const advertisersQuery = useQuery({
        queryKey: ["advertisers", { includeArchived: true }],
        queryFn: () => listAdvertisers({ includeArchived: true }),
        staleTime: 30_000,
        enabled: open,
    });

    const nameById = React.useCallback(
        (id: number | null): string => {
            if (id == null) return "Unassigned";
            const found = advertisersQuery.data?.find((a) => a.id === id);
            return found ? found.name : `#${id}`;
        },
        [advertisersQuery.data],
    );

    // Reset local state every time the dialog opens so a previously-aborted
    // edit doesn't leak into the next session.
    React.useEffect(() => {
        if (open) {
            setAdvertiserId(currentAdvertiserId);
            setReason("");
        }
    }, [open, currentAdvertiserId]);

    const mutation = useMutation({
        mutationFn: (vars: { advertiserId: number | null; reason: string }) =>
            assignToAdvertiser({
                entityType,
                entityId,
                advertiserId: vars.advertiserId,
                reason: vars.reason,
            }),
        onSuccess: () => {
            for (const key of INVALIDATE_KEYS[entityType]) {
                void queryClient.invalidateQueries({ queryKey: key });
            }
            void queryClient.invalidateQueries({ queryKey: ["advertisers"] });
            void queryClient.invalidateQueries({ queryKey: ["advertiser", "assignment-log"] });
            void queryClient.invalidateQueries({ queryKey: ["unassigned-count"] });
            if (successToast) {
                const t = successToast({
                    oldName: nameById(currentAdvertiserId),
                    newName: nameById(advertiserId),
                });
                if (t.variant === "warning") {
                    toast.warning(t.title, { description: t.description });
                } else {
                    toast.success(t.title, { description: t.description });
                }
            } else {
                toast.success("Advertiser updated");
            }
            setOpen(false);
            onSuccess?.();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Reassign failed")),
    });

    const trimmedReason = reason.trim();
    const dirty = advertiserId !== currentAdvertiserId;
    const canSave = dirty && trimmedReason.length > 0 && !mutation.isPending;

    const onSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!canSave) return;
        mutation.mutate({ advertiserId, reason: trimmedReason });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !mutation.isPending && setOpen(o)}>
            <div onClick={() => setOpen(true)} className="contents">
                {trigger}
            </div>
            <DialogContent data-testid={testId ?? "reassign-advertiser-dialog"}>
                <form onSubmit={onSubmit}>
                    <DialogHeader>
                        <DialogTitle>Reassign advertiser</DialogTitle>
                        <DialogDescription>
                            Pick a new advertiser for this {ENTITY_LABEL[entityType]}, or choose
                            “Unassigned” to clear the owner. The reason is recorded in the
                            assignment log.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="reassign-advertiser-select">Advertiser</Label>
                            <AdvertiserSelect
                                value={advertiserId}
                                onChange={(v) => {
                                    // The select can return undefined (the "All" sentinel)
                                    // but we don't expose that option here, so coerce to null.
                                    setAdvertiserId(v === undefined ? null : v);
                                }}
                                includeAll={false}
                                includeUnassigned
                                disabled={mutation.isPending}
                                data-testid="reassign-advertiser-select"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="reassign-advertiser-reason">
                                Reason <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="reassign-advertiser-reason"
                                placeholder="e.g. Acme bought this campaign from BrandX, effective 2026-06-01"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={mutation.isPending}
                                rows={3}
                                data-testid="reassign-advertiser-reason"
                            />
                            <p className="text-xs text-muted-foreground">
                                Required. Shown in the assignment-log audit trail.
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setOpen(false)}
                            disabled={mutation.isPending}
                            data-testid="reassign-advertiser-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!canSave}
                            data-testid="reassign-advertiser-save"
                        >
                            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                            Save
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Compact inline badge + edit-pencil trigger for the advertiser of an
 * entity. Renders the current advertiser name (or "Unassigned") and a
 * pencil icon that opens {@link ReassignAdvertiserPopover}. Use this
 * next to entity titles on detail pages — keeps every detail surface
 * consistent.
 */
export interface AdvertiserOwnerBadgeProps {
    entityType: AdvertiserEntityType;
    entityId: number;
    /** Current advertiser id from the entity DTO. Undefined → loading. */
    currentAdvertiserId: number | null | undefined;
    /** Hide the leading "Advertiser:" label (useful in tight headers). */
    compact?: boolean;
    /** Optional explicit testid prefix; defaults to entity type. */
    "data-testid"?: string;
    className?: string;
    /**
     * Optional override of the success toast — wired through to the
     * underlying {@link ReassignAdvertiserPopover}. Used by the
     * constructor header to call out snapshot-spend semantics.
     */
    successToast?: ReassignAdvertiserPopoverProps["successToast"];
    onSuccess?: () => void;
}

export function AdvertiserOwnerBadge({
    entityType,
    entityId,
    currentAdvertiserId,
    compact = false,
    "data-testid": testId,
    className,
    successToast,
    onSuccess,
}: AdvertiserOwnerBadgeProps): React.ReactNode {
    const tid = testId ?? `advertiser-badge-${entityType}-${entityId}`;
    // Resolve name from the cached list. Fetch is cheap and shared across
    // every badge on the page via the react-query cache.
    const advertisersQuery = useQuery({
        queryKey: ["advertisers", { includeArchived: true }],
        queryFn: () => listAdvertisers({ includeArchived: true }),
        staleTime: 30_000,
    });
    const currentName = React.useMemo<string>(() => {
        if (currentAdvertiserId == null) return "Unassigned";
        const a = advertisersQuery.data?.find((x) => x.id === currentAdvertiserId);
        return a ? a.name : `#${currentAdvertiserId}`;
    }, [advertisersQuery.data, currentAdvertiserId]);
    const isUnassigned = currentAdvertiserId == null;

    return (
        <ReassignAdvertiserPopover
            entityType={entityType}
            entityId={entityId}
            currentAdvertiserId={currentAdvertiserId ?? null}
            onSuccess={onSuccess}
            successToast={successToast}
            data-testid={`${tid}-dialog`}
            trigger={
                <button
                    type="button"
                    title="Reassign advertiser"
                    aria-label={`Advertiser: ${currentName}. Click to reassign.`}
                    data-testid={tid}
                    className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs hover:bg-muted",
                        className,
                    )}
                >
                    {!compact && (
                        <span className="text-muted-foreground">Advertiser:</span>
                    )}
                    <span
                        className={cn(
                            "max-w-[160px] truncate font-medium",
                            isUnassigned && "italic text-muted-foreground",
                        )}
                    >
                        {currentName}
                    </span>
                    <Pencil className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                </button>
            }
        />
    );
}
